import React, { useState, useEffect } from 'react';
import './SliderSettings.css';
import { DEVICES } from '../../constants/breakpoints';

const SliderSettings = ({ 
  element, 
  elements, 
  setElements, 
  setSelectedElement,
  selectedDevice
}) => {
  // Initialize device-specific settings from element or defaults
  const [deviceSettings, setDeviceSettings] = useState({
    [DEVICES.DESKTOP]: {
      slidesPerView: element.deviceSettings?.[DEVICES.DESKTOP]?.slidesPerView || element.slidesPerView || 1,
      slidesPerScroll: element.deviceSettings?.[DEVICES.DESKTOP]?.slidesPerScroll || element.slidesPerScroll || 1
    },
    [DEVICES.TABLET]: {
      slidesPerView: element.deviceSettings?.[DEVICES.TABLET]?.slidesPerView || element.slidesPerView || 1,
      slidesPerScroll: element.deviceSettings?.[DEVICES.TABLET]?.slidesPerScroll || element.slidesPerScroll || 1
    },
    [DEVICES.MOBILE]: {
      slidesPerView: element.deviceSettings?.[DEVICES.MOBILE]?.slidesPerView || element.slidesPerView || 1,
      slidesPerScroll: element.deviceSettings?.[DEVICES.MOBILE]?.slidesPerScroll || element.slidesPerScroll || 1
    }
  });

  // Initialize loop, autoplay, and delay settings
  const [loopEnabled, setLoopEnabled] = useState(element.attributes?.['data-loop'] === 'true');
  const [autoplayEnabled, setAutoplayEnabled] = useState(element.attributes?.['data-autoplay'] === 'true');
  const [autoplayDelay, setAutoplayDelay] = useState(parseInt(element.attributes?.['data-delay'] || '5000'));

  // Get current device settings with fallback to desktop or default values
  const getCurrentDeviceSettings = () => {
    if (!selectedDevice) return deviceSettings[DEVICES.DESKTOP] || { slidesPerView: 1, slidesPerScroll: 1 };
    return deviceSettings[selectedDevice] || deviceSettings[DEVICES.DESKTOP] || { slidesPerView: 1, slidesPerScroll: 1 };
  };
  
  const currentDeviceSettings = getCurrentDeviceSettings();

  // Update element attributes when settings change
  useEffect(() => {
    updateElementAttributes({
      'data-loop': loopEnabled.toString(),
      'data-autoplay': autoplayEnabled.toString(),
      'data-delay': autoplayDelay.toString()
    });
  }, [loopEnabled, autoplayEnabled, autoplayDelay]);

  const updateElementAttributes = (newAttributes) => {
    const updatedElement = {
      ...element,
      attributes: {
        ...element.attributes,
        ...newAttributes
      }
    };

    setElements(prevElements => 
      prevElements.map(el => el.id === element.id ? updatedElement : el)
    );

    if (element.id) {
      setSelectedElement(updatedElement);
    }
  };

  const addNewSlide = () => {
    const maskElement = elements.find(el => 
      el.parentId === element.id && 
      el.sliderRole === 'mask'
    );

    if (!maskElement) return;

    const newSlideId = Date.now();
    const newSlide = {
      id: newSlideId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      sliderRole: 'slide',
      styles: {
        flex: `0 0 ${100/currentDeviceSettings.slidesPerView}%`,
        height: '100%',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        fontSize: '24px',
        color: '#666'
      },
      text: `Slide ${maskElement.children.length + 1}`,
      children: [],
      parentId: maskElement.id,
      visibility: 'visible',
      isContainer: true
    };

    // Update mask's children
    const updatedMask = {
      ...maskElement,
      children: [...maskElement.children, newSlideId]
    };

    // Update elements array
    setElements(prevElements => [
      ...prevElements.map(el => el.id === maskElement.id ? updatedMask : el),
      newSlide
    ]);
  };

  const moveSlide = (direction) => {
    const maskElement = elements.find(el => 
      el.parentId === element.id && 
      el.sliderRole === 'mask'
    );

    if (!maskElement) return;

    const slides = maskElement.children.length;
    // Use builderId to find the specific mask element
    const maskNode = document.querySelector(`[data-builder-id="${maskElement.builderId}"]`);
    if (!maskNode) return;

    const currentSlide = parseInt(maskNode.getAttribute('data-current-slide') || 0);
    const slidesPerView = currentDeviceSettings.slidesPerView;
    const slidesPerScroll = currentDeviceSettings.slidesPerScroll;
    let newSlide;

    if (direction === 'prev') {
      if (currentSlide === 0 && loopEnabled) {
        // If at the beginning and loop is enabled, go to the end
        newSlide = slides - slidesPerView;
      } else {
        // Otherwise, navigate normally
        newSlide = Math.max(0, currentSlide - slidesPerScroll);
      }
    } else {
      if (currentSlide >= slides - slidesPerView && loopEnabled) {
        // If at the end and loop is enabled, go to the beginning
        newSlide = 0;
      } else {
        // Otherwise, navigate normally
        newSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
      }
    }
    
    maskNode.style.transform = `translateX(-${newSlide * (100/slidesPerView)}%)`;
    maskNode.setAttribute('data-current-slide', newSlide);
  };

  const updateDeviceSettings = (device, property, value) => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1) return;

    // Update local state
    setDeviceSettings(prev => ({
      ...prev,
      [device]: {
        ...prev[device],
        [property]: numValue
      }
    }));

    // Create or update deviceSettings in the element
    const updatedElement = {
      ...element,
      deviceSettings: {
        ...element.deviceSettings || {},
        [device]: {
          ...element.deviceSettings?.[device] || {},
          [property]: numValue
        }
      }
    };

    // Update slidesPerView and slidesPerScroll properties for backward compatibility
    if (device === selectedDevice) {
      if (property === 'slidesPerView') {
        updatedElement.slidesPerView = numValue;
      } else if (property === 'slidesPerScroll') {
        updatedElement.slidesPerScroll = numValue;
      }
    }

    // Update all elements
    setElements(prevElements => prevElements.map(el => {
      // Update slider container
      if (el.id === element.id) {
        return updatedElement;
      }
      
      // Update slides width when slidesPerView changes
      if (property === 'slidesPerView' && 
          el.sliderRole === 'slide' && 
          device === selectedDevice &&
          elements.find(parent => parent.id === el.parentId && parent.parentId === element.id)) {
        return {
          ...el,
          styles: {
            ...el.styles,
            flex: `0 0 ${100/numValue}%`
          }
        };
      }
      
      return el;
    }));
    
    // Update selected element
    if (element.id) {
      setSelectedElement(prev => ({
        ...prev,
        ...updatedElement
      }));
    }
    
    // Reset the scroll position if changing the current device's settings
    if (device === selectedDevice) {
      const maskElement = elements.find(el => el.parentId === element.id && el.sliderRole === 'mask');
      if (maskElement) {
        const maskNode = document.querySelector(`[data-builder-id="${maskElement.builderId}"]`);
        if (maskNode) {
          maskNode.style.transform = 'translateX(0)';
          maskNode.setAttribute('data-current-slide', 0);
        }
      }
    }
  };
  
  // Helper to render device-specific settings
  const renderDeviceSettings = (device) => {
    const settings = deviceSettings[device];
    const isCurrentDevice = device === selectedDevice;
    
    return (
      <div className={`device-settings ${isCurrentDevice ? 'current-device' : ''}`}>
        <h4>{device.charAt(0).toUpperCase() + device.slice(1)}</h4>
        <div className="config-item">
          <label>Slides Shown</label>
          <input 
            type="number" 
            min="1" 
            value={settings.slidesPerView} 
            onChange={(e) => updateDeviceSettings(device, 'slidesPerView', e.target.value)}
          />
        </div>
        
        <div className="config-item">
          <label>Slides Per Scroll</label>
          <input 
            type="number" 
            min="1" 
            value={settings.slidesPerScroll} 
            onChange={(e) => updateDeviceSettings(device, 'slidesPerScroll', e.target.value)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="slider-settings">
      <div className="setting-section">
        <h3 className="section-title">Slider Settings</h3>
        
        <div className="slider-configuration">
          <div className="responsive-settings">
            {renderDeviceSettings(DEVICES.DESKTOP)}
            {renderDeviceSettings(DEVICES.TABLET)}
            {renderDeviceSettings(DEVICES.MOBILE)}
          </div>
        </div>
        
        <div className="setting-section">
          <h4>Behavior</h4>
          
          <div className="config-item checkbox-item">
            <label>
              <input 
                type="checkbox" 
                checked={loopEnabled}
                onChange={(e) => setLoopEnabled(e.target.checked)}
              />
              Loop Slides
            </label>
            <span className="helper-text">When enabled, slides will loop back to the beginning after reaching the end</span>
          </div>
          
          <div className="config-item checkbox-item">
            <label>
              <input 
                type="checkbox" 
                checked={autoplayEnabled}
                onChange={(e) => setAutoplayEnabled(e.target.checked)}
              />
              Auto-Play
            </label>
            <span className="helper-text">Automatically advance to the next slide</span>
          </div>
          
          {autoplayEnabled && (
            <div className="config-item">
              <label>Delay (ms)</label>
              <input 
                type="number"
                min="1000"
                step="500"
                value={autoplayDelay}
                onChange={(e) => setAutoplayDelay(parseInt(e.target.value) || 5000)}
                className="delay-input"
              />
              <span className="helper-text">Time between slide changes in milliseconds</span>
            </div>
          )}
        </div>
        
        <div className="slider-controls">
          <button 
            className="add-slide-btn"
            onClick={addNewSlide}
          >
            <i className="fas fa-plus"></i>
            Add Slide
          </button>

          <div className="navigation-controls">
            <button 
              className="nav-btn prev-btn"
              onClick={() => moveSlide('prev')}
            >
              <i className="fas fa-chevron-left"></i>
              Previous
            </button>
            <button 
              className="nav-btn next-btn"
              onClick={() => moveSlide('next')}
            >
              Next
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <div className="slides-info">
          {elements.find(el => 
            el.parentId === element.id && 
            el.sliderRole === 'mask'
          )?.children.length || 0} Slides
        </div>
      </div>
    </div>
  );
};

export default SliderSettings; 